
import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import PrimaryButton from '../components/PrimaryButton';
import Alert from '@mui/material/Alert';

// Hide header/navbar for this page
const hideHeader = true;

function AcceptInvite({ tokenFromPath }) {
    // Hide header/navbar if possible (if using a layout component, set a flag)
    useEffect(() => {
        if (window && window.hideHeader) {
            window.hideHeader(true);
        }
        return () => {
            if (window && window.hideHeader) {
                window.hideHeader(false);
            }
        };
    }, []);
    const [searchParams] = useSearchParams();
    // Token from path (/client/invite/:token) takes precedence; otherwise from ?token=
    const token = tokenFromPath || searchParams.get('token');
    const [invite, setInvite] = useState(null);
    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [touched, setTouched] = useState({ password: false, confirmPassword: false });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setError('Invalid invite link.');
            return;
        }
        (async () => {
            try {
                const res = await api.get(`/api/public/invite-info?token=${token}`);
                setInvite(res.data);
            } catch (err) {
                setError('Invalid or expired invite link.');
            }
        })();
    }, [token]);

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
    const handleBlur = field => setTouched(t => ({ ...t, [field]: true }));

    const valid = form.password.length >= 8 && form.password === form.confirmPassword;

    const handleSubmit = async e => {
        e.preventDefault();
        setTouched({ password: true, confirmPassword: true });
        if (!valid) return;
        setSubmitting(true);
        setError('');
        try {
            await api.post('/api/public/accept-invite', { token, password: form.password });
            setSuccess('Account created! You can now log in.');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to accept invite.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component="main">
            <Grid container spacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4} lg={3}></Grid>
                <Grid item xs={12} md={8} lg={6}>
                    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
                            <Box
                                component="img"
                                src="/ryslogo.png"
                                alt="Retool Your School"
                                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
                            />
                        </Box>
                        <Box sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Accept Invite</Typography>
                            {invite && !error && (
                                <>
                                    {/* Show who sent the invite and their school if available */}
                                    {(invite.inviterFirstName || invite.inviterLastName || invite.schoolName) && (
                                        <Typography sx={{ mb: 1 }}>
                                            This invite was sent by
                                            {invite.inviterFirstName || invite.inviterLastName ? (
                                                <> <b>{invite.inviterFirstName} {invite.inviterLastName}</b></>
                                            ) : null}
                                            {invite.schoolName ? (
                                                <> from <b>{invite.schoolName}</b></>
                                            ) : null}
                                            .
                                        </Typography>
                                    )}
                                    <Typography sx={{ mb: 2 }}>
                                        You are accepting an invite for <b>{invite.inviteeEmail}</b> ({invite.inviteeFirstName} {invite.inviteeLastName})
                                    </Typography>
                                </>
                            )}
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                            {/* Only show the form if there is no error and no success */}
                            {!error && !success && (
                                <Box component="form" onSubmit={handleSubmit} noValidate>
                                    <TextField label="Password" name="password" type="password" value={form.password} onChange={handleChange} onBlur={() => handleBlur('password')} fullWidth sx={{ mb: 2 }} required error={touched.password && form.password.length < 8} helperText={touched.password && form.password.length < 8 ? 'Password must be at least 8 characters' : ''} />
                                    <TextField label="Confirm Password" name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} onBlur={() => handleBlur('confirmPassword')} fullWidth sx={{ mb: 2 }} required error={touched.confirmPassword && form.confirmPassword !== form.password} helperText={touched.confirmPassword && form.confirmPassword !== form.password ? 'Passwords do not match' : ''} />
                                    <PrimaryButton type="submit" disabled={!valid || submitting}>Accept Invite</PrimaryButton>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AcceptInvite;
